import type { CSSProperties, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog'
import './app-compat-surfaces.css'

export interface AppDialogProps {
  readonly actions?: ReactNode
  readonly children?: ReactNode
  readonly contentSx?: CSSProperties
  readonly description?: ReactNode
  readonly dividers?: boolean
  readonly fullScreen?: boolean
  readonly fullWidth?: boolean
  readonly maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  readonly onClose: () => void
  readonly open: boolean
  readonly title?: ReactNode
}

export function AppDialog({
  actions,
  children,
  contentSx,
  description,
  dividers = true,
  fullScreen = false,
  fullWidth = true,
  maxWidth = 'sm',
  onClose,
  open,
  title,
}: AppDialogProps) {
  const hasDialogHeader = Boolean(title || description)

  const invokingElementRef = useRef<HTMLElement | null>(null)
  const previousOpenRef = useRef(false)

  useEffect(() => {
    if (open && !previousOpenRef.current && typeof document !== 'undefined') {
      const activeElement = document.activeElement
      invokingElementRef.current = activeElement instanceof HTMLElement ? activeElement : null
    }

    previousOpenRef.current = open
  }, [open])

  const restoreInvokingFocus = () => {
    const invokingElement = invokingElementRef.current
    if (!invokingElement?.isConnected) return false

    invokingElement.focus()
    return true
  }

  const handleCloseAutoFocus = (event: Event) => {
    if (!restoreInvokingFocus()) return
    event.preventDefault()
  }

  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          restoreInvokingFocus()
          onClose()
        }
      }}
      open={open}
    >
      <DialogContent
        className="appCompatDialog"
        data-dividers={dividers ? 'true' : 'false'}
        data-full-screen={fullScreen ? 'true' : 'false'}
        data-full-screen-content-only={
          fullScreen && !hasDialogHeader && !actions ? 'true' : 'false'
        }
        data-full-width={fullWidth ? 'true' : 'false'}
        data-max-width={maxWidth || 'none'}
        hideClose
        onCloseAutoFocus={handleCloseAutoFocus}
        style={fullScreen ? {
          inset: 0,
          maxWidth: 'none',
          width: '100vw',
          height: '100dvh',
          transform: 'none',
          translate: 'none',
          border: 0,
          borderRadius: 0,
        } : undefined}
      >
        {title || description ? (
          <DialogHeader className="appCompatDialogHeader">
            {title ? <DialogTitle>{title}</DialogTitle> : null}
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
        ) : null}
        <div className="appCompatDialogBody" style={contentSx}>
          {children}
        </div>
        {actions ? <DialogFooter className="appCompatDialogActions">{actions}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  )
}
